import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, User, Mail, Lock, Trash2, ArrowLeft } from "lucide-react";
import { Link } from "wouter";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function FieldSettingsPage() {
  usePageTitle("Settings");
  const { user, refreshUser, logout } = useAuth();
  const { toast } = useToast();

  const mustChange = user?.mustChangePassword ?? false;

  // --- Profile ---
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [profileSaving, setProfileSaving] = useState(false);

  const handleProfileSave = async () => {
    setProfileSaving(true);
    try {
      const res = await fetch(`${BASE}/api/field/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() || undefined, email: email.trim() || undefined }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to save");
      await refreshUser();
      toast({ title: "Profile updated" });
    } catch (err: unknown) {
      toast({ title: (err as Error).message ?? "Failed to save", variant: "destructive" });
    } finally {
      setProfileSaving(false);
    }
  };

  // --- Password ---
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const handlePasswordSave = async () => {
    if (newPw !== confirmPw) {
      toast({ title: "New passwords don't match", variant: "destructive" });
      return;
    }
    if (newPw.length < 8) {
      toast({ title: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }
    setPwSaving(true);
    try {
      const res = await fetch(`${BASE}/api/field/me/password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to change password");
      await refreshUser();
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      toast({ title: "Password changed" });
    } catch (err: unknown) {
      toast({ title: (err as Error).message ?? "Failed to change password", variant: "destructive" });
    } finally {
      setPwSaving(false);
    }
  };

  // --- Delete account ---
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${BASE}/api/field/me`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete account");
      toast({ title: "Account deleted" });
      await logout();
    } catch {
      toast({ title: "Failed to delete account", variant: "destructive" });
      setDeleting(false);
    }
  };

  return (
    <div style={{ maxWidth: "28rem", margin: "0 auto", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Header */}
      <div>
        {!mustChange && (
          <div style={{ marginBottom: "0.75rem" }}>
            <Link href="/field">
              <button style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                className="hover:text-foreground transition-colors">
                <ArrowLeft style={{ width: "1rem", height: "1rem" }} />
                Back
              </button>
            </Link>
          </div>
        )}
        <h1 style={{ fontSize: "1.375rem", fontWeight: 700 }}>Account Settings</h1>
        {mustChange && (
          <p style={{ marginTop: "0.375rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
            Please set a new password before continuing.
          </p>
        )}
      </div>

      {/* Profile section — hidden when mustChangePassword (only show password form) */}
      {!mustChange && (
        <section style={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600, fontSize: "0.9375rem" }}>
            <User style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))" }} />
            Profile
          </div>
          <div>
            <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "hsl(var(--muted-foreground))" }}>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} style={{ marginTop: "0.375rem" }} />
          </div>
          <div>
            <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "hsl(var(--muted-foreground))" }}>
              <Mail style={{ width: "0.75rem", height: "0.75rem", display: "inline", marginRight: "0.25rem" }} />
              Email
            </Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ marginTop: "0.375rem" }} />
          </div>
          <Button onClick={handleProfileSave} disabled={profileSaving || (!name.trim() && !email.trim())}>
            {profileSaving && <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />}
            Save Profile
          </Button>
        </section>
      )}

      {/* Password section */}
      <section style={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600, fontSize: "0.9375rem" }}>
          <Lock style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))" }} />
          {mustChange ? "Set New Password" : "Change Password"}
        </div>
        {mustChange && (
          <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginTop: "-0.25rem" }}>
            Enter the temporary password that was emailed to you, then choose a new one.
          </p>
        )}
        <div>
          <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "hsl(var(--muted-foreground))" }}>
            {mustChange ? "Temporary Password" : "Current Password"}
          </Label>
          <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} style={{ marginTop: "0.375rem" }} />
        </div>
        <div>
          <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "hsl(var(--muted-foreground))" }}>New Password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 8 characters" style={{ marginTop: "0.375rem" }} />
        </div>
        <div>
          <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "hsl(var(--muted-foreground))" }}>Confirm New Password</Label>
          <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} style={{ marginTop: "0.375rem" }} />
        </div>
        <Button onClick={handlePasswordSave} disabled={pwSaving || !currentPw || !newPw || !confirmPw}>
          {pwSaving && <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />}
          {mustChange ? "Set Password & Continue" : "Change Password"}
        </Button>
      </section>

      {/* Delete account — hidden when forcing password change */}
      {!mustChange && (
        <section style={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--destructive) / 0.3)", borderRadius: "0.75rem", padding: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600, fontSize: "0.9375rem", color: "hsl(var(--destructive))", marginBottom: "0.5rem" }}>
            <Trash2 style={{ width: "1rem", height: "1rem" }} />
            Delete Account
          </div>
          <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginBottom: "1rem" }}>
            Your time logs and photos will remain in the project records. Your login credentials will be permanently removed.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" style={{ width: "100%" }}>Delete My Account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your login will be permanently removed. Your time logs and photos will stay in the project. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  style={{ backgroundColor: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}
                >
                  {deleting ? <Loader2 style={{ width: "0.875rem", height: "0.875rem" }} className="animate-spin mr-1" /> : null}
                  Delete Account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}
    </div>
  );
}
